/** Match setup: difficulty, culture bonuses, and how a game can be won. */

export type Difficulty = "easy" | "normal" | "hard";
export type WinRule = "standard" | "score";
export type MissionId = "skirmish" | "first-hearth" | "the-ford" | "high-ground";
export type DipStance = "ally" | "neutral" | "enemy";

export type CivId =
  | "assyria"
  | "babylon"
  | "choson"
  | "egypt"
  | "greece"
  | "minoan"
  | "hittite"
  | "phoenicia"
  | "sumer"
  | "persia"
  | "shang"
  | "yamato"
  | "carthage"
  | "macedon"
  | "palmyra"
  | "rome";

export type CivDef = {
  id: CivId;
  name: string;
  note: string;
  walk?: number;
  archerCd?: number;
  stone?: number;
  gold?: number;
  wood?: number;
  hunt?: number;
  farm?: number;
  villagerHp?: number;
  wallHp?: number;
  towerRange?: number;
  work?: number;
  startFood?: number;
  villagerCost?: number;
  shipHp?: number;
  buildCost?: number;
};

export const CIVS: CivDef[] = [
  { id: "assyria", name: "Assyria", note: "Villagers walk faster. Archers loose faster.", walk: 1.1, archerCd: 0.8 },
  { id: "babylon", name: "Babylon", note: "Stone miners work faster. Walls and towers are tougher.", stone: 1.2, wallHp: 1.75 },
  { id: "choson", name: "Choson", note: "Swordsmen are heartier. Towers see farther.", towerRange: 2 },
  { id: "egypt", name: "Egypt", note: "Gold miners work faster. Chariots and priests reach farther.", gold: 1.2 },
  { id: "greece", name: "Greece", note: "Academy footmen are cheaper and quicker. Ships are quicker.", walk: 1.05 },
  { id: "minoan", name: "Minoa", note: "Farms hold more food. Ships cost less.", farm: 60 },
  { id: "hittite", name: "Hatti", note: "Archers hit harder. Siege engines are tougher.", archerCd: 0.9 },
  { id: "phoenicia", name: "Phoenicia", note: "Woodcutters work faster. Elephants cost less.", wood: 1.15 },
  { id: "sumer", name: "Sumer", note: "Villagers are tougher. Farms hold much more food.", villagerHp: 15, farm: 125 },
  { id: "persia", name: "Persia", note: "Hunters work faster. Elephants move faster.", hunt: 1.3 },
  { id: "shang", name: "Shang", note: "Villagers are cheaper. Walls are tougher.", villagerCost: 40, wallHp: 1.75 },
  { id: "yamato", name: "Yamato", note: "Villagers walk faster. Horses cost less.", walk: 1.1 },
  { id: "carthage", name: "Carthage", note: "Elephants and academy footmen are tougher.", villagerHp: 5 },
  { id: "macedon", name: "Macedon", note: "Soldiers see farther. Siege costs less.", towerRange: 1 },
  { id: "palmyra", name: "Palmyra", note: "Villagers cost more, work faster, and start with extra food.", villagerCost: 75, work: 1.25, startFood: 100 },
  { id: "rome", name: "Rome", note: "Most buildings cost less. Towers cost half.", buildCost: 0.85 },
];

export function civById(id: string | undefined): CivDef {
  return CIVS.find((c) => c.id === id) || CIVS[0];
}

export function isDifficulty(v: string): v is Difficulty {
  return v === "easy" || v === "normal" || v === "hard";
}

export function isMission(v: string): v is MissionId {
  return v === "skirmish" || v === "first-hearth" || v === "the-ford" || v === "high-ground";
}

export const MISSIONS: Array<{ id: MissionId; name: string; note: string; difficulty: Difficulty }> = [
  { id: "skirmish", name: "Skirmish", note: "Four corners. You choose the difficulty.", difficulty: "normal" },
  { id: "first-hearth", name: "First Hearth", note: "Build 2 houses and gather 400 food.", difficulty: "easy" },
  { id: "the-ford", name: "The Ford", note: "Hold the ruin across the shallows.", difficulty: "normal" },
  { id: "high-ground", name: "High Ground", note: "A hard enemy. Destroy their town.", difficulty: "hard" },
];
